/**
 * FinishChatDialog - Modal para finalizar conversas (mockup app.html #mdlFinish:
 * `.mdl lg` central 640px, header ícone verde, reason-grid 2 colunas com dot colorido).
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Icon } from '@/shared/icons'
interface FinishChatDialogProps {
  conversationId: string;
  conversationSubject?: string;
  isOpen: boolean;
  onClose: () => void;
  onFinish: (reason?: string, notes?: string) => void;
  isLoading?: boolean;
}

// Valores = contrato do backend; labels/ordem/cores = mockup (dot colorido).
const closeReasons = [
  { value: 'resolved', label: 'Resolvido', dot: '#10b981' },
  { value: 'spam', label: 'Spam', dot: '#ef4444' },
  { value: 'duplicate', label: 'Duplicada', dot: '#f59e0b' },
  { value: 'no_response', label: 'Sem resposta', dot: '#6b7280' },
  { value: 'transferred', label: 'Transferida', dot: '#3b82f6' },
  { value: 'other', label: 'Outro', dot: '#8b5cf6' },
];

const FinishChatDialog: React.FC<FinishChatDialogProps> = ({
  conversationId: _conversationId,
  conversationSubject,
  isOpen,
  onClose,
  onFinish,
  isLoading = false,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('resolved');
  const [notes, setNotes] = useState('');

  const handleFinish = () => {
    onFinish(selectedReason, notes.trim() || undefined);
  };

  // ✅ Atalho Enter: confirma encerramento (ignora quando foco está no textarea de notas)
  useEffect(() => {
    if (!isOpen || isLoading) return;

    const handleEnterKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'TEXTAREA') return;
      e.preventDefault();
      handleFinish();
    };

    window.addEventListener('keydown', handleEnterKey);
    return () => window.removeEventListener('keydown', handleEnterKey);
  }, [isOpen, isLoading, selectedReason, notes]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl" closeOnEscape={!isLoading}>
      <ModalHeader onClose={isLoading ? undefined : onClose}>
        {/* mockup: mdl-ic verde #ecfdf5/#059669 */}
        <div
          className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
          style={{ backgroundColor: '#ecfdf5', color: '#059669' }}
        >
          <Icon name="check-circle" className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-[16px] font-bold truncate" style={{ color: '#1e293b' }}>Finalizar conversa</h2>
          {conversationSubject && (
            <p className="text-xs truncate" style={{ color: '#64748b' }}>{conversationSubject}</p>
          )}
        </div>
      </ModalHeader>

      <ModalContent>
        {/* Reason grid (mockup: 2 colunas, botão .reason = dot + label) */}
        <label className="block text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>
          Motivo do encerramento
        </label>
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          {closeReasons.map((reason) => {
            const isSelected = selectedReason === reason.value;
            return (
              <button
                key={reason.value}
                onClick={() => setSelectedReason(reason.value)}
                disabled={isLoading}
                className="flex items-center gap-2.5 text-left h-11 px-3 rounded-[10px] border transition-all disabled:opacity-50"
                style={{
                  borderColor: isSelected ? reason.dot : '#e5e7eb',
                  backgroundColor: isSelected ? `${reason.dot}14` : '#ffffff',
                  color: '#1e293b',
                  boxShadow: isSelected ? `inset 0 0 0 1px ${reason.dot}` : 'none',
                }}
                data-testid={`finish-reason-${reason.value}`}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ backgroundColor: reason.dot }} />
                <span className="text-sm font-medium truncate">{reason.label}</span>
              </button>
            );
          })}
        </div>

        {/* Notes */}
        <Textarea
          label="Observações finais (opcional)"
          description="Ficam registradas no histórico da conversa"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isLoading}
          placeholder="Adicione observações..."
          rows={3}
          resize="none"
        />

        {/* Info box (mockup: .infobox azul #f0f9ff/#bae6fd, título + 4 bullets) */}
        <div
          className="mt-4 rounded-[10px] border"
          style={{
            backgroundColor: '#f0f9ff',
            borderColor: '#bae6fd',
            color: '#075985',
            padding: '11px 13px',
            fontSize: '12.5px',
            lineHeight: 1.55,
          }}
        >
          <b className="block mb-1">O que acontece ao finalizar</b>
          · A conversa vai para "Últimos Finalizados"<br />
          · O contato pode reabrir enviando nova mensagem<br />
          · As métricas de atendimento são consolidadas<br />
          · O agente de IA volta ao estado padrão do canal
        </div>
      </ModalContent>

      <ModalFooter>
        <Button onClick={onClose} disabled={isLoading} variant="secondary" size="md">
          Cancelar
        </Button>
        <Button
          onClick={handleFinish}
          disabled={isLoading}
          variant="success"
          size="md"
          icon={isLoading ? 'loader' : 'check-circle'}
          loading={isLoading}
          data-testid="finish-confirm"
        >
          {isLoading ? 'Finalizando...' : 'Finalizar conversa'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FinishChatDialog;
